// @flow

import * as React from 'react';
import {InlineLink} from 'sail/InlineLink';
import {CompactTooltip} from 'sail/Tooltip';
import Moment from 'react-moment';

import CopyAction from './CopyAction';

export const COLORS = {
  string: '#85d996',
  number: '#e4ab80',
  boolean: '#7fd3ed',
  default: 'white',
};

export const ID_PREFIX_MAP = {
  acct: '/accounts/',
  ch: '/charges/',
  cn: '/credit_notes/',
  cs: '/checkout/sessions/',
  cus: '/customers/',
  dp: '/disputes/',
  evt: '/events/',
  fee: '/application_fees/',
  file: '/files/',
  iauth: '/issuing/authorizations/',
  ic: '/issuing/cards/',
  ich: '/issuing/cardholders/',
  idp: '/issuing/disputes/',
  ii: '/invoice_items/',
  in: '/invoices/',
  ipi: '/issuing/transactions/',
  issfr: '/radar/early_fraud_warnings/',
  link: '/file_links/',
  or: '/orders/',
  orret: '/order_returns/',
  pi: '/payment_intents/',
  plan: '/plans/',
  pm: '/payment_methods/',
  po: '/payouts/',
  price: '/prices/',
  prod: '/products/',
  prv: '/reviews/',
  py: '/charges/',
  re: '/refunds/',
  rsl: '/radar/value_lists/',
  rsli: '/radar/value_list_items/',
  seti: '/setup_intents/',
  si: '/subscription_items/',
  sku: '/skus/',
  sqr: '/sigma/scheduled_query_runs/',
  src: '/sources/',
  sub: '/subscriptions/',
  sub_sched: '/subscription_schedules/',
  tml: '/terminal/locations/',
  tmr: '/terminal/readers/',
  tok: '/tokens/',
  tr: '/transfers/',
  tu: '/topups/',
  txi: '/tax_ids/',
  txn: '/balance_transactions/',
  txr: '/tax_rates/',
  we: '/webhook_endpoints/',
};

export const VERTICAL_SPACING = 6;

export const getSurroundingChars = (isArray?: boolean) =>
  isArray ? ['[', ']'] : ['{', '}'];

export const isObject = (value: any) =>
  !Array.isArray(value) && typeof value === 'object' && value !== null;

const buildPathForID = (id: string) => {
  const prefix = id.split('_')[0];
  const path = ID_PREFIX_MAP[prefix];

  return `/test${path}${id}`;
};

const isURL = (str) => /^http[s]?:\/\//.test(str);
const isIDLike = (str) =>
  Object.keys(ID_PREFIX_MAP).includes(str.split('_')[0]);
const isTimestampLike = (key, num) =>
  ['created', 'updated', 'timestamp'].includes(key) && typeof num === 'number';

export const buildDefaultRenderValue = ({
  dashboardHost,
}: {
  dashboardHost: string,
}) => ({key, value}: {key: string, value: any}) => {
  switch (typeof value) {
    case 'string': {
      const normalized = value.replace(/"/g, '');

      if (isURL(normalized)) {
        return (
          <InlineLink
            href={normalized}
            target="_blank"
            rel="nooppener"
            className="JsonLink"
          >
            {`"${value}"`}
          </InlineLink>
        );
      } else if (isIDLike(normalized)) {
        return (
          <div className="JsonResourceID">
            <InlineLink
              href={`${dashboardHost}${buildPathForID(normalized)}`}
              target="_blank"
              rel="nooppener"
              className="JsonLink"
            >
              {`"${value}"`}
            </InlineLink>
            <CopyAction
              data={normalized}
              className="JsonResourceID-clipboard"
            />
          </div>
        );
      } else {
        return `"${value}"`;
      }
    }
    case 'number': {
      if (isTimestampLike(key, value)) {
        return (
          <CompactTooltip
            variableWidth
            context={String(value)}
            label={<Moment date={value * 1000} />}
          />
        );
      } else {
        return String(value);
      }
    }
    default:
      return String(value);
  }
};
